import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { API_URLS } from '../config/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { 
  BookOpen, 
  Clock, 
  User, 
  Eye,
  Heart,
  Share2,
  Search,
  Filter,
  TrendingUp,
  Calendar,
  ArrowRight,
  ShoppingCart,
  Globe,
  Server,
  Bot,
  Zap,
  Star
} from 'lucide-react';
import NewsletterInline from './NewsletterInline';

const BlogSystem = ({ sectionContent }) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [blogPosts, setBlogPosts] = useState([]);
  
  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const res = await fetch(API_URLS.BLOGS);
        if (!res.ok) throw new Error('Blog fetch failed');
        const data = await res.json();
        
        // API'den gelen veriyi kontrol et
        let posts = [];
        if (data.status === 'success' && data.blogs) {
          posts = data.blogs; // Yeni API formatı
        } else if (Array.isArray(data)) {
          posts = data; // Eski API formatı
        } else if (data.data) {
          posts = data.data; // Alternatif format
        }
        
        // Sort by newest and take only first 3
        const sortedPosts = posts.sort((a, b) => 
          new Date(b.published_at || b.publishDate || b.created_at) - 
          new Date(a.published_at || a.publishDate || a.created_at)
        );
        
        setBlogPosts(sortedPosts.slice(0, 3));
      } catch (e) {
        // Silently handle error
      }
    };
    fetchBlogs();
  }, []);

  /* const blogPosts = [
    // E-ticaret Category
    {
      id: 1,
      title: 'E-ticaret Sitesi Kurma Rehberi: Sıfırdan Başlangıç',
      slug: 'eticaret-sitesi-kurma-rehberi-sifirdan-baslangic',
      excerpt: 'E-ticaret dünyasına adım atmak isteyenler için kapsamlı başlangıç rehberi. Platform seçiminden ödeme sistemlerine kadar her şey.',
      content: `E-ticaret sektörü hızla büyüyor ve doğru adımlarla sizin de bu pastadan pay almanız mümkün. Bu rehberde e-ticaret sitesi kurmanın temellerini öğreneceksiniz.

## E-ticaret Nedir?

E-ticaret, ürün ve hizmetlerin internet üzerinden satışını ifade eder. Türkiye'de e-ticaret hacmi yıllık %30 büyüyor ve 2024'te 500 milyar TL'yi aşması bekleniyor.

## Platform Seçimi

### 1. Shopify
- Kolay kurulum
- Aylık $29'dan başlayan fiyatlar
- 100+ ücretsiz tema

### 2. WooCommerce
- WordPress tabanlı
- Ücretsiz başlangıç
- Sınırsız özelleştirme

### 3. Opencart
- Açık kaynak kodlu
- Türkçe dil desteği
- Gelişmiş stok yönetimi

## Gerekli Adımlar

1. **İş Planı Hazırlama**
2. **Platform Seçimi**
3. **Domain ve Hosting**
4. **Site Tasarımı**
5. **Ürün Yükleme**
6. **Ödeme Sistemi**
7. **Kargo Entegrasyonu**
8. **SEO Optimizasyonu**

## Başarı İpuçları

- Mobil uyumlu tasarım yapın
- Hızlı loading süresi sağlayın
- Güvenlik sertifikası kullanın
- Müşteri yorumlarına önem verin

Website Al ekibi olarak e-ticaret sitenizi 7 gün içinde hazır hale getirebiliriz. Ücretsiz danışmanlık için bizimle iletişime geçin.`,
      category: 'ecommerce',
      author: 'Ahmet Yılmaz',
      authorRole: 'E-ticaret Uzmanı',
      publishDate: '2024-01-15',
      readTime: '8 dakika',
      views: 2543,
      likes: 89,
      level: 'Başlangıç',
      image: '/blog/ecommerce-guide.jpg',
      tags: ['e-ticaret', 'başlangıç', 'shopify', 'woocommerce']
    },
    {
      id: 2,
      title: 'E-ticaret SEO: Satışları 10 Kat Artıran Teknikler',
      slug: 'eticaret-seo-satislari-10-kat-artiran-teknikler',
      excerpt: 'E-ticaret sitelerinde SEO nasıl yapılır? Ürün sayfalarından kategori sayfalarına kadar profesyonel SEO teknikleri.',
      content: `E-ticaret SEO'su, klasik SEO'dan farklı yaklaşımlar gerektirir. Bu makalede satışlarınızı katlamaya yardımcı olacak profesyonel teknikleri öğreneceksiniz.

## E-ticaret SEO Temelleri

E-ticaret sitelerinde SEO yaparken dikkat edilmesi gereken temel alanlar:

### 1. Ürün Sayfası SEO
- Benzersiz ürün açıklamaları
- Yüksek kaliteli görseller
- Müşteri yorumları
- Schema markup kullanımı

### 2. Kategori Sayfası Optimizasyonu
- Detaylı kategori açıklamaları
- İç bağlantı stratejisi
- Filtre dostu URL yapısı
- Sayfalama optimizasyonu

## Teknik SEO Gereksinimleri

### Site Hızı Optimizasyonu
- Görsel sıkıştırma
- CDN kullanımı
- Minification
- Lazy loading

### Mobil Optimizasyon
- Responsive tasarım
- Touch-friendly arayüz
- Hızlı mobil yükleme
- AMP implementasyonu

## Anahtar Kelime Stratejisi

### Ürün Odaklı Anahtar Kelimeler
- "ürün adı + satın al"
- "ürün adı + fiyat"
- "ürün adı + yorumları"
- "ürün adı + özellikleri"

## İçerik Marketing

### Blog Entegrasyonu
- Ürün kullanım rehberleri
- Sektör haberları
- Karşılaştırma makaleleri
- Video içerikler

## Başarı Ölçümleri

- Organik trafik artışı
- Dönüşüm oranı iyileşmesi
- Anahtar kelime sıralamaları
- Gelir artışı

Website Al SEO uzmanları ile e-ticaret sitenizin organik trafiğini 6 ayda %300 artırabilirsiniz.`,
      category: 'ecommerce',
      author: 'Ayşe Kaya',
      authorRole: 'SEO Uzmanı',
      publishDate: '2024-01-20',
      readTime: '12 dakika',
      views: 1876,
      likes: 134,
      level: 'İleri',
      image: '/blog/ecommerce-seo.jpg',
      tags: ['seo', 'e-ticaret', 'traffic', 'optimizasyon']
    },
    
    // E-ihracat Category
    {
      id: 3,
      title: 'E-ihracat ile Global Pazarlara Açılma Rehberi',
      slug: 'eihracat-ile-global-pazarlara-acilma-rehberi',
      excerpt: 'Türkiye\'den dünyaya satış yapmak artık çok kolay. E-ihracat süreçleri, platformları ve başarı hikayeleri.',
      content: `E-ihracat, Türk işletmelerinin global pazarlara açılmasının en hızlı yolu. Bu rehberde e-ihracatın A'sından Z'sine kadar her şeyi öğreneceksiniz.

## E-ihracat Nedir?

E-ihracat, ürün ve hizmetlerin elektronik platformlar üzerinden yurt dışına satılması işlemidir. Türkiye'nin e-ihracat hacmi 2023'te 2.5 milyar dolara ulaştı.

## E-ihracat Avantajları

### Ekonomik Avantajlar
- Düşük başlangıç maliyeti
- Geniş pazar erişimi
- 7/24 satış imkanı
- Döviz kazancı

### Operasyonel Avantajlar
- Coğrafi sınır yok
- Stok maliyeti düşük
- Dijital marketing imkanları
- Veri analitiği desteği

## Başlıca E-ihracat Platformları

### 1. Amazon Global
- 200+ ülke erişimi
- FBA desteği
- Güçlü lojistik
- Yüksek müşteri güveni

### 2. eBay International
- Müzayede modeli
- C2C ve B2C seçenekleri
- Düşük komisyon
- Kolay başlangıç

### 3. Alibaba.com
- B2B odaklı
- Toptan satış
- Güçlü Çin pazarı
- Trade Assurance garantisi

## Gerekli Belgeler

### Yasal Gereksinimler
- Vergi levhası
- KEP adresi
- İthalat/İhracat kodu
- ETGB kaydı

### Platform Gereksinimleri
- İngilizce katalog
- Uluslararası ödeme hesabı
- Kargo anlaşmaları
- Müşteri hizmetleri

## Başarı Stratejileri

### Ürün Seçimi
- Türkiye'nin güçlü olduğu sektörler
- Nakliye maliyeti düşük ürünler
- Yüksek kalite/fiyat oranı
- Benzersiz/niche ürünler

### Fiyatlandırma
- Rekabet analizi
- Kargo+gümrük maliyetleri
- Vergi hesaplamaları
- Kâr marjı planlama

## Lojistik Çözümler

### Kargo Seçenekleri
- PTT Kargo International
- UPS/DHL/FedEx
- Özel kargo firmaları
- Dropshipping modeli

Website Al e-ihracat uzmanları ile 30 günde global pazarlarda satış yapmaya başlayabilirsiniz.`,
      category: 'eexport',
      author: 'Mehmet Özkan',
      authorRole: 'E-ihracat Danışmanı',
      publishDate: '2024-01-10',
      readTime: '15 dakika',
      views: 3421,
      likes: 156,
      level: 'Orta',
      image: '/blog/eexport-guide.jpg',
      tags: ['e-ihracat', 'global', 'amazon', 'ihracat']
    },

    // Hosting & Domain Category  
    {
      id: 4,
      title: 'Web Hosting Seçimi: Performans ve Güvenlik Rehberi',
      slug: 'web-hosting-secimi-performans-ve-guvenlik-rehberi',
      excerpt: 'Doğru hosting seçimi web sitenizin başarısını doğrudan etkiler. Hosting türleri, özellikler ve seçim kriterleri.',
      content: `Web hosting seçimi, online varlığınızın temelini oluşturur. Bu rehberde profesyonel hosting seçim kriterlerini öğreneceksiniz.

## Hosting Türleri

### Paylaşımlı Hosting
**Avantajları:**
- Uygun fiyat (₺50-200/ay)
- Kolay yönetim
- Teknik destek
- cPanel kontrolü

**Dezavantajları:**
- Sınırlı kaynak
- Performans dalgalanmaları
- Güvenlik riskleri
- Özelleştirme kısıtları

### VPS Hosting
**Avantajları:**
- Dedicated kaynaklar
- Root erişimi
- Scalability
- Daha iyi performans

**Fiyat Aralığı:** ₺200-1000/ay

### Dedicated Server
**Avantajları:**
- Tam kontrol
- Maximum performans
- Yüksek güvenlik
- Özel konfigürasyon

**Fiyat Aralığı:** ₺2000-10000/ay

## Hosting Seçim Kriterleri

### 1. Performance Metrikleri
- **Uptime:** Minimum %99.9
- **Loading Speed:** <3 saniye
- **Bandwidth:** Sınırsız tercih
- **Storage:** SSD depolama

### 2. Güvenlik Özellikleri
- SSL sertifikası (ücretsiz)
- Malware tarama
- Backup sistemi
- Firewall koruması
- DDoS protection

### 3. Teknik Destek
- 7/24 canlı destek
- Türkçe destek
- Ticketing sistemi
- Telefon desteği
- Ortalama yanıt süresi

## Türkiye'nin En İyi Hosting Firmaları

### 1. Turhost
- ₺49/ay başlangıç
- %99.9 uptime garantisi
- Ücretsiz SSL
- Türkçe destek

### 2. Natro
- ₺59/ay başlangıç
- CloudFlare CDN
- Otomatik backup
- WordPress optimize

### 3. Radore
- Enterprise çözümler
- Kurumsal destek
- Gelişmiş güvenlik
- Custom çözümler

## Domain Yönetimi

### Domain Seçimi
- .com.tr / .com tercihli
- Marka uyumlu
- Kısa ve akılda kalıcı
- SEO dostu

### DNS Yönetimi
- A Record ayarları
- CNAME yapılandırması
- MX Record e-posta
- TTL optimizasyonu

## Performans Optimizasyonu

### Caching Sistemleri
- Browser caching
- Server-side caching
- CDN kullanımı
- Database caching

### Monitoring Tools
- Google PageSpeed
- GTmetrix
- Pingdom
- Uptime Robot

Website Al hosting uzmanları ile web sitenizin performansını %200 artırabilir, güvenliğini maximum seviyeye çıkarabilirsiniz.`,
      category: 'hosting',
      author: 'Can Demir',
      authorRole: 'Sistem Yöneticisi',
      publishDate: '2024-01-18',
      readTime: '10 dakika',
      views: 2134,
      likes: 98,
      level: 'Orta',
      image: '/blog/hosting-guide.jpg',
      tags: ['hosting', 'performance', 'güvenlik', 'domain']
    },

    // Yapay Zeka Category
    {
      id: 5,
      title: 'ChatGPT ile İş Süreçlerini Otomatikleştirme',
      slug: 'chatgpt-ile-is-sureclerini-otomatiklestirme',
      excerpt: 'Yapay zeka araçları ile iş süreçlerinizi otomatikleştirin. ChatGPT, Claude ve diğer AI araçlarının işletmelerdeki kullanımı.',
      content: `Yapay zeka artık sci-fi değil, günlük iş hayatımızın bir parçası. Bu rehberde AI araçları ile iş süreçlerinizi nasıl otomatikleştireceğinizi öğreneceksiniz.

## Yapay Zeka ve İş Dünyası

2024 itibariyle yapay zeka araçları kullanım oranları:
- %87 işletme ChatGPT kullanıyor
- %45 müşteri hizmetlerinde AI
- %67 content creation'da AI
- %23 tam otomatik süreçler

## İş Süreçlerinde AI Kullanım Alanları

### 1. Müşteri Hizmetleri
**Chatbot Entegrasyonu:**
- 7/24 müşteri desteği
- Sıkça sorulan sorular
- Ticket yönlendirme
- Çoklu dil desteği

**Avantajlar:**
- %70 maliyet azaltma
- %90 daha hızlı yanıt
- %95 müşteri memnuniyeti

### 2. İçerik Üretimi
**Blog Yazıları:**
- SEO optimize içerik
- Çoklu dil çeviri
- A/B test metinleri
- Social media postları

**E-posta Marketing:**
- Personalized kampanyalar
- Subject line optimizasyonu
- Newsletter içerikleri
- Autoresponder metinleri

### 3. Veri Analizi
**Business Intelligence:**
- Satış trend analizi
- Müşteri davranış analizi
- Maliyet optimizasyonu
- Predictive analytics

## Popüler AI Araçları

### 1. ChatGPT Plus ($20/ay)
**Özellikler:**
- GPT-4 erişimi
- Plugin desteği
- Code interpreter
- Web browsing

**Kullanım Alanları:**
- Yaratıcı yazım
- Problem çözme
- Kod yazma
- Veri analizi

### 2. Claude 3 (Anthropic)
**Özellikler:**
- 200K token limiti
- PDF okuma
- Görsel analiz
- Güvenli conversations

### 3. Midjourney ($10-60/ay)
**Özellikler:**
- AI görsel üretimi
- V6 model
- Style referansları
- Commercial license

### 4. Microsoft Copilot
**Entegrasyonlar:**
- Word, Excel, PowerPoint
- Teams meetings
- Outlook e-mails
- SharePoint search

## AI Implementation Roadmap

### Faz 1: Pilot Projeler (1-2 ay)
- Basit automation
- Team training
- ROI measurement
- Use case validation

### Faz 2: Department Integration (3-6 ay)
- Process optimization
- Custom solutions
- Performance metrics
- Scale-up planning

### Faz 3: Enterprise Deployment (6-12 ay)
- Company-wide rollout
- Advanced integrations
- Custom model training
- Full automation

## ROI Hesaplama

### Maliyet Azaltımı
- İnsan kaynağı tasarrufu
- Operasyonel efficiency
- Error reduction
- Time savings

### Gelir Artışı
- Daha hızlı delivery
- Better customer experience
- New service offerings
- Market expansion

### Örnek Hesaplama:
**Müşteri hizmetleri AI'ı:**
- Maliyet: $500/ay
- Tasarruf: $3000/ay
- ROI: %500

## Güvenlik ve Etik

### Data Privacy
- KVKV uyumlu
- Encrypted communication
- On-premise options
- Access controls

### Ethical AI Usage
- Transparency
- Bias prevention
- Human oversight
- Responsible automation

Website Al AI uzmanları ile işletmenizde yapay zeka transformasyonunu 30 günde başlatabilir, 6 ayda tam automation sağlayabilirsiniz.`,
      category: 'ai',
      author: 'Dr. Elif Yıldız',
      authorRole: 'AI Uzmanı',
      publishDate: '2024-01-25',
      readTime: '14 dakika',
      views: 4321,
      likes: 245,
      level: 'İleri',
      image: '/blog/ai-automation.jpg',
      tags: ['ai', 'automation', 'chatgpt', 'iş süreçleri']
    }
  ]; */

  // Handle search - redirect to search page with blog filter
  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/arama?q=${encodeURIComponent(searchQuery.trim())}&type=blog`);
    }
  };

  return (
    <div className="w-full">
      {/* Header */}
      <div className="text-center mb-12">
        <div className="inline-flex items-center space-x-3 bg-[#7A5AFF]/15 text-[#7A5AFF] px-6 py-3 rounded-full text-lg font-medium mb-6">
          <BookOpen className="w-6 h-6" />
          <span>{sectionContent?.badge_text || 'Website Al Blog'}</span>
        </div>
        
        <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
          <span className="bg-gradient-to-r from-black to-[#7A5AFF] bg-clip-text text-transparent">
            {sectionContent?.title || 'Dijital Dünya Rehberi'}
          </span>
        </h2>
        
        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
          {sectionContent?.description || 'E-ticaret, hosting, yapay zeka ve dijital pazarlama alanlarında uzman makaleler. Başlangıç seviyesinden profesyonel seviyeye kadar rehberler.'}
        </p>
        {sectionContent?.items && sectionContent.items.length > 0 && (
          <div className="flex flex-wrap justify-center gap-x-8 gap-y-2 mt-6">
            {sectionContent.items.map((item, idx) => (
              <div key={idx} className="flex items-center gap-2 text-gray-700">
                <Star className="w-5 h-5 text-[#7A5AFF] flex-shrink-0" />
                <span>{typeof item === 'string' ? item : (item?.text || item?.label || '')}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Search and View All Button */}
      <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4 mb-8">
        {/* Search - Left Side */}
        <form onSubmit={handleSearch} className="w-full md:w-full">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 z-10" />
            <Input
              type="text"
              placeholder="Blog yazılarında ara..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="blog-input pl-10 pr-12 py-3 border-2 border-gray-200 focus:border-[#7A5AFF] focus:ring-2 focus:ring-[#7A5AFF]/20 w-full placeholder:text-gray-400"
            />
            <Button
              type="submit"
              size="sm"
              className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-[#7A5AFF] hover:bg-[#8b6bff] text-white h-8 px-4 text-sm"
            >
              Ara
            </Button>
          </div>
        </form>

        {/* View All Button - Right Side */}
        <Link to="/blog" className="w-full md:w-auto">
          <Button 
            variant="outline" 
            size="lg"
            className="border-2 border-[#7A5AFF] text-[#7A5AFF] hover:bg-[#7A5AFF] hover:text-white px-6 whitespace-nowrap w-full md:w-auto"
          >
            Tüm Blog Yazılarını Gör
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </Link>
      </div>

      {/* Blog Posts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8">
        {blogPosts.length === 0 && (
          <div className="col-span-full text-center py-12">
            <p className="text-gray-500">Blog yazısı yükleniyor...</p>
          </div>
        )}
        {blogPosts.map((blog) => (
          <Card key={blog.id} className="group hover:shadow-xl transition-shadow duration-300 overflow-hidden flex flex-col h-full">
            {/* Blog Image */}
            <div className="relative h-48 bg-gradient-to-br from-black to-[#7A5AFF] overflow-hidden flex-shrink-0">
              <div className="absolute inset-0 bg-black/20"></div>
              <div className="absolute top-4 left-4">
                <Badge className="bg-white/90 text-gray-800">
                  {blog.category || 'Blog'}
                </Badge>
              </div>
              {blog.level && (
                <div className="absolute top-4 right-4">
                  <Badge variant="outline" className="bg-white/90 text-gray-800">
                    {blog.level}
                  </Badge>
                </div>
              )}
            </div>

            <CardContent className="p-6 flex flex-col flex-grow">
              {/* Blog Meta */}
              <div className="flex items-center text-sm text-gray-500 mb-3 flex-shrink-0">
                <User className="w-4 h-4 mr-1" />
                <span className="mr-4">{blog.author || 'Website Al'}</span>
                <Clock className="w-4 h-4 mr-1" />
                <span className="mr-4">{blog.readTime || '5 dk'}</span>
                <Calendar className="w-4 h-4 mr-1" />
                <span>{new Date(blog.publishDate || blog.published_at).toLocaleDateString('tr-TR')}</span>
              </div>

              {/* Blog Title */}
              <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-[#7A5AFF] transition-colors line-clamp-2 flex-shrink-0">
                {blog.title}
              </h3>

              {/* Blog Excerpt - Fixed height */}
              <div className="mb-4 flex-shrink-0" style={{ minHeight: '72px' }}>
                <p className="text-gray-600 line-clamp-3">
                  {blog.excerpt || 'Blog içeriğini okumak için devamını okuyun...'}
                </p>
              </div>

              {/* Blog Tags - Fixed height */}
              <div className="flex flex-wrap gap-2 mb-4 flex-shrink-0" style={{ minHeight: '32px' }}>
                {(blog.tags || []).slice(0, 3).map((tag, index) => (
                  <Badge key={index} variant="secondary" className="text-xs">
                    {tag}
                  </Badge>
                ))}
              </div>

              {/* Spacer to push content to bottom */}
              <div className="flex-grow"></div>

              {/* Blog Stats - Always at bottom */}
              <div className="flex items-center justify-between text-sm text-gray-500 mb-4 flex-shrink-0">
                <div className="flex items-center space-x-4">
                  <span className="flex items-center space-x-1">
                    <Eye className="w-4 h-4" />
                    <span>{(blog.views || 0).toLocaleString()}</span>
                  </span>
                  <span className="flex items-center space-x-1">
                    <Heart className="w-4 h-4" />
                    <span>{blog.likes || 0}</span>
                  </span>
                </div>
                <Button variant="ghost" size="sm">
                  <Share2 className="w-4 h-4" />
                </Button>
              </div>

              {/* Read More Button - Always at bottom */}
              <Link to={`/blog/${blog.id || blog.slug}`} className="flex-shrink-0">
                <Button className="w-full bg-gradient-to-r from-[#7A5AFF] to-[#5a3fd9] hover:from-[#8b6bff] hover:to-[#6b4de6]">
                  <span>Devamını Oku</span>
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Newsletter Subscription - Haftalık Dijital Insights */}
      <Card className="mt-16 bg-gradient-to-r from-black via-[#1a0a2e] to-[#7A5AFF] text-white border-0 shadow-xl">
        <CardContent className="p-8 text-center">
          <div className="space-y-6">
            <div className="w-16 h-16 bg-[#7A5AFF]/30 rounded-full flex items-center justify-center mx-auto">
              <Zap className="w-8 h-8 text-[#7A5AFF]" />
            </div>
            
            <div>
              <h3 className="text-2xl font-bold mb-2">
                Haftalık Dijital Insights
              </h3>
              <p className="text-white/85 mb-6">
                En güncel dijital pazarlama, e-ticaret ve teknoloji haberlerini e-postanızda alın. 
                Haftada sadece 1 e-posta, spam yok!
              </p>
            </div>

            <NewsletterInline />

            <div className="flex items-center justify-center space-x-6 text-sm text-white/70">
              <span>📧 25.000+ aktif abone</span>
              <span>⭐ 4.9 memnuniyet puanı</span>
              <span>🚀 %95 açılma oranı</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default BlogSystem;